import { createTsForm, useTsController } from '@ts-react/form';
import { isNaN } from 'lodash';
import { z } from 'zod';

import { parseCronString } from '#/utils/parseCronString';

import { Input } from '~/ui/input';
import { Label } from '~/ui/label';

function Field({ children }) {
  return <div className="mt-2 flex flex-col space-y-1">{children}</div>;
}

const DefaultSchedules = [
  {
    disabled: false,
    label: 'Every minute',
    value: '* * * * *',
  },
  {
    disabled: false,
    label: 'Every 5 minutes',
    value: '*/5 * * * *',
  },
  {
    disabled: false,
    label: 'Every 15 minutes',
    value: '*/15 * * * *',
  },
  {
    disabled: false,
    label: 'Every 30 minutes',
    value: '*/30 * * * *',
  },
  {
    disabled: false,
    label: 'Every hour',
    value: '0 * * * *',
  },
  {
    disabled: false,
    label: 'Every 2 hours',
    value: '0 */2 * * *',
  },
  {
    disabled: false,
    label: 'Every 4 hours',
    value: '0 */4 * * *',
  },
  {
    disabled: false,
    label: 'Twice daily at noon and midnight',
    value: '0 */12 * * *',
  },
  {
    disabled: false,
    label: 'Daily at midnight',
    value: '0 0 * * *',
  },
];
const DefaultScheduleValues = DefaultSchedules.map((s) => s.value);

function TextField(props) {
  console.debug('[TextField] props:', props);
  const { name } = props;

  const {
    field: { onChange, value = '' },
    error,
  } = useTsController<string>();

  if (name === 'schedule') {
    const parsedValue = parseCronString(value);

    return (
      <Field>
        <Label htmlFor={name}>{name}</Label>
        {/* <p className="text-primary">{parsedValue}</p> */}
        <select
          id={name}
          onChange={(e) => onChange(e.target.value)}
          value={value}
          className="rounded-md"
        >
          {!DefaultScheduleValues.includes(value) ? (
            <option key={value} label={parsedValue} value={value} />
          ) : null}
          {DefaultSchedules.map((scheduleOption) => (
            <option key={scheduleOption.label} {...scheduleOption} />
          ))}
        </select>
        <Input
          id={name}
          onChange={(e) => onChange(e.target.value)}
          value={value}
        />
        {error && error.errorMessage}
      </Field>
    );
  }

  return (
    <Field>
      <Label htmlFor={name}>{name}</Label>
      <Input
        id={name}
        onChange={(e) => onChange(e.target.value)}
        value={value}
      />
      {error && error.errorMessage}
    </Field>
  );
}

function CheckBox(props: { name: string }) {
  // console.debug('CheckBox', props);
  const { name } = props;

  const {
    field: { onChange, value },
  } = useTsController<boolean>();

  return (
    <Field>
      <Label htmlFor={name}>
        {name}
        <Input
          id={name}
          onChange={(e) => onChange(e.target.checked)}
          checked={value ? value : false}
          type="checkbox"
        />
      </Label>
    </Field>
  );
}

function NumberField(props: { req: number }) {
  // console.debug('NumberField', props);
  const { name, req } = props;

  const {
    field: { onChange, value },
    error,
  } = useTsController<number>();

  return (
    <Field>
      <Label htmlFor={name}>{name}</Label>
      <Input
        type="number"
        inputMode="numeric"
        id={name}
        value={value != null ? String(value) : ''}
        onChange={(e) => {
          const value = Number(e.target.value);
          if (isNaN(value)) return;
          else onChange(value);
        }}
        style={{
          'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
            {
              '-webkit-appearance': 'none',
              margin: 0,
            },
          'input[type=number]': { '-moz-appearance': 'textfield' },
        }}
      />
      {error && error.errorMessage}
    </Field>
  );
}

function ArraySubForm(props: { req: Record<any, any> }) {
  console.debug('ArraySubForm', props);
  const { name, req } = props;

  return (
    <Field>
      <Label>{name}</Label>
      {Object.entries(req ?? {}).map(([key, value]) => {
        console.debug({ key, value });
        return (
          <div key={key}>
            <span>{key}</span>
            <span>{value}</span>
          </div>
        );
      })}
    </Field>
  );
}

function ArrayField<T>() {
  const {
    field: { onChange, value },
  } = useTsController<T[]>();
}

function SubForm(props: { req: Record<any, any> }) {
  console.debug('SubForm', props);
  const { name, req } = props;

  return (
    <Field>
      <Label>{name}</Label>
      {Object.entries(req ?? {}).map(([key, value]) => {
        console.debug({ key, value });
        return (
          <div key={key}>
            <span>{key}</span>
            <span>{value}</span>
          </div>
        );
      })}
    </Field>
  );
}

// create the mapping
const mapping = [
  [z.string(), TextField],
  [z.boolean(), CheckBox],
  [z.number(), NumberField],
  [z.array(z.any()), ArraySubForm],
  // [z.union(z.array(z.any())), SubForm],
  [z.record(z.any()), SubForm],
  // [z.object(z.any()), TextField],
] as const; // 👈 `as const` is necessary

// A typesafe React component
export const DynamicForm = createTsForm(mapping);
export default DynamicForm;
